import { z } from 'zod';

import { PaginationSchema } from '../../../pagination.js';

const taxonomySlug = z
  .string()
  .trim()
  .min(1)
  .max(100)
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/);

/** Object-storage key persisted on write (BR-PRD-25). Not a URL. */
const logoStorageKey = z
  .string()
  .trim()
  .min(1)
  .max(1024)
  .regex(/^[a-zA-Z0-9][a-zA-Z0-9/_.-]*$/)
  .refine((value) => !value.includes('://'), {
    message: 'logoStorageKey must be a storage key, not a URL',
  });

export const RegisterBrandBodySchema = z.object({
  name: z.string().trim().min(1).max(255),
  slug: taxonomySlug,
  logoStorageKey: logoStorageKey.nullable().optional(),
});

export const RegisterBrandResponseSchema = z.object({
  brandId: z.uuid(),
});

export const UpdateBrandBodySchema = z
  .object({
    name: z.string().trim().min(1).max(255).optional(),
    slug: taxonomySlug.optional(),
    logoStorageKey: logoStorageKey.nullable().optional(),
  })
  .refine((body) => Object.keys(body).length > 0, { message: 'At least one field is required' });

export const ArchiveBrandBodySchema = z.object({
  reason: z.string().trim().max(500).optional(),
});

export const ListBrandsQuerySchema = PaginationSchema;

export const BrandListItemSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  slug: z.string(),
  /** Resolved download URL for display; null when brand has no logo (BR-PRD-25). */
  logoUrl: z.string().nullable(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});

export const ListBrandsResponseSchema = z.object({
  items: z.array(BrandListItemSchema),
  pagination: z.object({
    cursor: z.string().nullable(),
    hasMore: z.boolean(),
  }),
});

export type RegisterBrandBody = z.infer<typeof RegisterBrandBodySchema>;
export type RegisterBrandResponse = z.infer<typeof RegisterBrandResponseSchema>;
export type UpdateBrandBody = z.infer<typeof UpdateBrandBodySchema>;
export type ArchiveBrandBody = z.infer<typeof ArchiveBrandBodySchema>;
export type ListBrandsQuery = z.infer<typeof ListBrandsQuerySchema>;
export type ListBrandsResponse = z.infer<typeof ListBrandsResponseSchema>;
